/** API istemcisi: aynı kökenden /v1, çerezli oturum, CSRF başlığı, problem+json hataları, MFA yeniden doğrulama. */

export interface FieldError {
  field: string;
  code: string;
  message: string;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    public title: string,
    public params: Record<string, string | number> = {},
    public errors: FieldError[] = [],
    public requestId?: string,
  ) {
    super(title);
  }
  fieldError(field: string): string | undefined {
    return this.errors.find((e) => e.field === field || e.field.startsWith(`${field}.`))?.message;
  }
}

let currentLocale = 'tr';
export function setApiLocale(locale: string) {
  currentLocale = locale;
}

type StepUpHandler = () => Promise<boolean>;
let stepUpHandler: StepUpHandler | null = null;
export function registerStepUpHandler(h: StepUpHandler | null) {
  stepUpHandler = h;
}

type AuthLostHandler = (code: string) => void;
let authLost: AuthLostHandler | null = null;
export function registerAuthLostHandler(h: AuthLostHandler | null) {
  authLost = h;
}

function csrfToken(): string {
  const m = /(?:^|;\s*)zt_csrf=([^;]+)/.exec(document.cookie);
  return m ? decodeURIComponent(m[1]!) : '';
}

/**
 * Pencere başına tekrar anahtarı: aynı istek (yöntem + adres + gövde) tekrarlanırsa aynı anahtar kullanılır (çift
 * tıklama ve ağ yeniden denemesi güvenli); kullanıcı hatayı düzeltip **farklı** bir istek gönderirse yeni anahtar
 * üretilir. Sabit anahtar, reddedilen isteğin düzeltilmiş hâlini "aynı anahtar, farklı istek" (409) yapıyordu.
 */
export class IdempotencyKey {
  private last: { request: string; key: string } | null = null;
  for(request: string): string {
    if (!this.last || this.last.request !== request) this.last = { request, key: newIdempotencyKey() };
    return this.last.key;
  }
}

export interface RequestOptions {
  idempotencyKey?: string | IdempotencyKey;
  ifMatch?: number;
  /** FormData gönderimi (dosya yükleme). */
  form?: FormData;
  /** Bu istek MFA yeniden doğrulama gerektirirse kullanıcıya sorulmasın. */
  noStepUp?: boolean;
}

async function raw<T>(
  method: string,
  path: string,
  body?: unknown,
  opts: RequestOptions = {},
  retried = false,
): Promise<T> {
  const headers: Record<string, string> = { 'accept-language': currentLocale, accept: 'application/json' };
  if (method !== 'GET') headers['x-csrf-token'] = csrfToken();
  if (opts.idempotencyKey)
    headers['idempotency-key'] =
      typeof opts.idempotencyKey === 'string'
        ? opts.idempotencyKey
        : opts.idempotencyKey.for(`${method} ${path} ${JSON.stringify(body ?? null)}`);
  if (opts.ifMatch !== undefined) headers['if-match'] = String(opts.ifMatch);
  let payload: BodyInit | undefined;
  if (opts.form) payload = opts.form;
  else if (body !== undefined) {
    headers['content-type'] = 'application/json';
    payload = JSON.stringify(body);
  }
  const res = await fetch(path, { method, headers, body: payload, credentials: 'include' });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const data = text ? (JSON.parse(text) as unknown) : null;
  if (!res.ok) {
    const p = (data ?? {}) as {
      code?: string;
      title?: string;
      params?: Record<string, string | number>;
      errors?: FieldError[];
      requestId?: string;
    };
    const err = new ApiError(
      res.status,
      p.code ?? 'internal',
      p.title ?? res.statusText,
      p.params ?? {},
      p.errors ?? [],
      p.requestId,
    );
    if (err.code === 'step_up_required' && !retried && !opts.noStepUp && stepUpHandler) {
      const ok = await stepUpHandler();
      if (ok) return raw<T>(method, path, body, opts, true);
    }
    if (['unauthenticated', 'mfa_required', 'mfa_setup_required', 'no_organization'].includes(err.code))
      authLost?.(err.code);
    throw err;
  }
  return data as T;
}

/** Dosya indirme (ZIP vb.): hata yanıtı JSON'dur; hassas işlemde MFA yeniden doğrulaması sorulur. */
async function rawBlob(path: string, retried = false): Promise<{ blob: Blob; fileName: string | null }> {
  const res = await fetch(path, { credentials: 'include', headers: { 'accept-language': currentLocale } });
  if (res.ok) {
    const cd = res.headers.get('content-disposition') ?? '';
    return { blob: await res.blob(), fileName: /filename="([^"]+)"/.exec(cd)?.[1] ?? null };
  }
  const p = ((await res.json().catch(() => ({}))) ?? {}) as {
    code?: string;
    title?: string;
    params?: Record<string, string | number>;
  };
  const err = new ApiError(res.status, p.code ?? 'internal', p.title ?? res.statusText, p.params ?? {});
  if (err.code === 'step_up_required' && !retried && stepUpHandler && (await stepUpHandler()))
    return rawBlob(path, true);
  throw err;
}

export const api = {
  get: <T>(path: string) => raw<T>('GET', path),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) => raw<T>('POST', path, body, opts),
  put: <T>(path: string, body?: unknown, opts?: RequestOptions) => raw<T>('PUT', path, body, opts),
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions) => raw<T>('PATCH', path, body, opts),
  del: <T>(path: string, opts?: RequestOptions) => raw<T>('DELETE', path, undefined, opts),
  upload: <T>(path: string, form: FormData) => raw<T>('POST', path, undefined, { form }),
  blob: (path: string) => rawBlob(path),
};

export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

/** API'den gelen JSON: tarih alanları metin olarak gelir. */
export type Jsonify<T> = T extends Date
  ? string
  : T extends Array<infer U>
    ? Array<Jsonify<U>>
    : T extends object
      ? { [K in keyof T]: Jsonify<T[K]> }
      : T;
